/**
 * 渠道结构健康诊断 - API 接口层
 * 对接后端 Spring MVC Controller
 * 注意：后端所有接口返回 ResultEntity 格式 { status, message, data }
 *
 * 说明：统一走项目封装的 httpFetch（src/api/httpFetch），
 * 携带 token / 拦截器 / noFormat 处理逻辑，与全项目其他接口保持一致。
 * 接口返回 { status, message, data }，其中 status 为成功标识（如 10000）。
 */
import fetch from '@/api/httpFetch'

const BASE = '/hcm/diagnosis'

/**
 * 通用请求方法（支持 get/post/put/delete）
 * @param {string} method  请求方法
 * @param {string} url     接口路径（相对 /diagnosis）
 * @param {object} data    POST/PUT 请求体（JSON 序列化发送）
 * @param {object} params  GET/DELETE 查询参数
 * @param {number} timeout 请求超时时间（毫秒），缺省用全局默认值
 */
function request(method, url, data = {}, params = {}, timeout) {
  const ctx = `${method.toUpperCase()} ${BASE}${url}`
  // POST/PUT 带请求体：后端以 @RequestBody 接收 JSON，
  // 需覆盖全局 form-urlencoded 序列化，改为 application/json
  const isBody = method === 'post' || method === 'put'
  const config = {
    url: `${BASE}${url}`,
    method,
    params,
    noFormat: true,
    responseType: 'text'
  }
  if (timeout) config.timeout = timeout
  if (isBody) {
    config.data = JSON.stringify(data)
    config.headers = { 'Content-Type': 'application/json' }
  } else {
    config.data = data
  }
  return fetch(config).then(res => {
    // 防御：请求实际失败（如 HTTP 非2xx）时不应透传 undefined
    if (res === undefined || res === null) {
      throw new Error(`${ctx}：请求未成功返回（请检查接口地址/代理/后端服务）`)
    }
    return typeof res === 'string' ? JSON.parse(res) : res
  }).catch(err => {
    // 给错误补充接口上下文，方便定位是哪个接口报错
    if (!err.__ctx) err.__ctx = ctx
    throw err
  })
}

export default {
  /**
   * AI识别品牌行业
   * GET /diagnosis/detectIndustry?brandName=xxx
   * @returns {Promise<{status, message, data:{industry: string}}>}
   */
  detectIndustry(brandName) {
    return request('get', '/detectIndustry', {}, { brandName })
  },

  /**
   * 查询BRM渠道数据
   * GET /diagnosis/brmData?brandName=xxx
   * @returns {Promise<{status, message, data: {
   *   brand, totalStores, authorizedStores, grayListStores, dealerCount,
   *   maxDealerDepth, authRate, platformsFound, platformsNotFound,
   *   platformDetails, brmScores: { qid: { score, note } }
   * }}>}
   */
  getBrmData(brandName) {
    return request('get', '/brmData', {}, { brandName })
  },

  /**
   * 触发AI搜索
   * POST /diagnosis/aiSearch
   * 说明：AI 搜索耗时较长，超时时间设为 5 分钟
   * @returns {Promise<{status, message, data: {
   *   industry, reasons: {qid: string}, findings: {key: {found, detail}},
   *   scores: {qid: number}
   * }}>}
   */
  aiSearch(brandName, industry) {
    return request('post', '/aiSearch', { brandName, industry }, {}, 5 * 60 * 1000)
  },

  /**
   * 保存诊断结果
   * POST /diagnosis/save
   */
  saveDiagnosis(data) {
    return request('post', '/save', data)
  },

  /**
   * 获取历史诊断报告
   * GET /diagnosis/report?sessionId=xxx
   */
  getReport(sessionId) {
    return request('get', '/report', {}, { sessionId })
  },

  /**
   * 诊断历史列表
   * GET /diagnosis/history?page=1&size=20
   */
  getHistory(page = 1, size = 20) {
    return request('get', '/history', {}, { page, size })
  },

  /**
   * 修改单项评分
   * PUT /diagnosis/updateScore
   */
  updateScore(sessionId, questionId, score) {
    return request('put', '/updateScore', { sessionId, questionId, score })
  },

  /**
   * 删除诊断记录
   * DELETE /diagnosis/delete?sessionId=xxx
   */
  deleteDiagnosis(sessionId) {
    return request('delete', '/delete', {}, { sessionId })
  }
}
